/**
 * Options for Add/Edit vehicle (aligned with mobile app design).
 * Manufacturer is stored in DB column `make`; model in `model`.
 */

export const VEHICLE_MANUFACTURERS = [
  'Audi',
  'BMW',
  'Bentley',
  'Buler',
  'BYD',
  'Cadillac',
  'Captain',
  'Carver',
  'CF Moto',
  'Changan',
  'Chevrolet',
  'Chrysler',
  'Citroën',
  'Cupra',
  'Dacia',
  'Dodge',
  'DS',
  'Ferrari',
  'Fiat',
  'Ford',
  'Genesis',
  'Honda',
  'Hyundai',
  'Infiniti',
  'Isuzu',
  'Jaguar',
  'Jeep',
  'Kia',
  'Lamborghini',
  'Land Rover',
  'Lexus',
  'Lotus',
  'MG',
  'Maserati',
  'Mazda',
  'McLaren',
  'Mercedes-Benz',
  'Mini',
  'Mitsubishi',
  'Nissan',
  'Opel',
  'Peugeot',
  'Polestar',
  'Porsche',
  'Renault',
  'Rolls-Royce',
  'Saab',
  'Seat',
  'Skoda',
  'Smart',
  'Subaru',
  'Suzuki',
  'Tesla',
  'Toyota',
  'Volkswagen',
  'Volvo',
].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));

/** Common model lineups by make (taxi / fleet oriented). Unknown makes fall back to `Other`. */
export const VEHICLE_MODELS_BY_MAKE = {
  Audi: [
    'A1',
    'A3',
    'A4',
    'A5',
    'A6',
    'A7',
    'A8',
    'Q2',
    'Q3',
    'Q4 e-tron',
    'Q5',
    'Q7',
    'Q8',
    'e-tron',
    'e-tron GT',
    'RS 6',
    'RS 7',
    'Other',
  ],
  BMW: [
    '1 Series',
    '2 Series',
    '3 Series',
    '4 Series',
    '5 Series',
    '6 Series',
    '7 Series',
    '8 Series',
    'X1',
    'X2',
    'X3',
    'X4',
    'X5',
    'X6',
    'X7',
    'Z4',
    'i4',
    'iX',
    'iX1',
    'iX3',
    'Other',
  ],
  Bentley: ['Continental', 'Flying Spur', 'Bentayga', 'Other'],
  Buler: ['Other'],
  BYD: ['Atto 3', 'Dolphin', 'Seal', 'Tang', 'Han', 'Other'],
  Cadillac: ['CT4', 'CT5', 'CT6', 'XT4', 'XT5', 'XT6', 'Escalade', 'Lyriq', 'Other'],
  Captain: ['Other'],
  Carver: ['Other'],
  'CF Moto': ['Other'],
  Changan: ['CS35', 'CS55', 'CS75', 'UNI-K', 'Other'],
  Chevrolet: ['Spark', 'Aveo', 'Cruze', 'Malibu', 'Trax', 'Equinox', 'Traverse', 'Tahoe', 'Suburban', 'Bolt', 'Other'],
  Chrysler: ['300', 'Pacifica', 'Voyager', 'Other'],
  'Citroën': ['C3', 'C4', 'C5', 'Berlingo', 'Spacetourer', 'ë-C4', 'Other'],
  Cupra: ['Formentor', 'Born', 'Leon', 'Ateca', 'Other'],
  Dacia: ['Sandero', 'Logan', 'Duster', 'Jogger', 'Spring', 'Other'],
  Dodge: ['Charger', 'Challenger', 'Durango', 'Hornet', 'Other'],
  DS: ['DS 3', 'DS 4', 'DS 7', 'DS 9', 'Other'],
  Ferrari: ['Roma', 'Portofino', '296', 'SF90', 'Other'],
  Fiat: ['500', '500e', 'Panda', 'Tipo', 'Doblo', 'Other'],
  Ford: ['Fiesta', 'Focus', 'Mondeo', 'Mustang Mach-E', 'Kuga', 'Puma', 'Explorer', 'Transit', 'Tourneo', 'Other'],
  Genesis: ['G70', 'G80', 'G90', 'GV60', 'GV70', 'GV80', 'Other'],
  Honda: ['Civic', 'Accord', 'HR-V', 'CR-V', 'e:Ny1', 'Other'],
  Hyundai: ['i10', 'i20', 'i30', 'Ioniq 5', 'Ioniq 6', 'Tucson', 'Santa Fe', 'Kona', 'Other'],
  Infiniti: ['Q50', 'Q60', 'QX50', 'QX55', 'QX60', 'Other'],
  Isuzu: ['D-Max', 'MU-X', 'Other'],
  Jaguar: ['XE', 'XF', 'F-Pace', 'E-Pace', 'I-Pace', 'Other'],
  Jeep: ['Renegade', 'Compass', 'Cherokee', 'Grand Cherokee', 'Wrangler', 'Avenger', 'Other'],
  Kia: ['Picanto', 'Ceed', 'Proceed', 'Optima', 'Stinger', 'Niro', 'Sportage', 'Sorento', 'EV6', 'EV9', 'Other'],
  Lamborghini: ['Huracán', 'Urus', 'Other'],
  'Land Rover': ['Range Rover', 'Range Rover Sport', 'Velar', 'Evoque', 'Discovery', 'Defender', 'Other'],
  Lexus: ['CT', 'IS', 'ES', 'LS', 'NX', 'RX', 'RZ', 'UX', 'Other'],
  Lotus: ['Emira', 'Eletre', 'Other'],
  MG: ['MG3', 'MG4', 'MG5', 'ZS', 'HS', 'Marvel R', 'Other'],
  Maserati: ['Ghibli', 'Quattroporte', 'Levante', 'Grecale', 'Other'],
  Mazda: ['Mazda2', 'Mazda3', 'Mazda6', 'CX-3', 'CX-30', 'CX-5', 'CX-60', 'MX-30', 'Other'],
  McLaren: ['GT', 'Artura', 'Other'],
  'Mercedes-Benz': [
    'A-Class',
    'B-Class',
    'C-Class',
    'CLA',
    'CLS',
    'E-Class',
    'EQE',
    'EQS',
    'S-Class',
    'GLA',
    'GLB',
    'GLC',
    'GLE',
    'GLS',
    'G-Class',
    'V-Class',
    'EQB',
    'EQC',
    'Other',
  ],
  Mini: ['3-door', '5-door', 'Clubman', 'Countryman', 'Electric', 'Other'],
  Mitsubishi: ['Space Star', 'ASX', 'Eclipse Cross', 'Outlander', 'Other'],
  Nissan: ['Micra', 'Leaf', 'Juke', 'Qashqai', 'X-Trail', 'Ariya', 'NV200', 'Other'],
  Opel: ['Corsa', 'Astra', 'Insignia', 'Mokka', 'Grandland', 'Combo', 'Vivaro', 'Other'],
  Peugeot: ['108', '208', '308', '508', '2008', '3008', '5008', 'e-208', 'e-2008', 'Rifter', 'Traveller', 'Other'],
  Polestar: ['Polestar 2', 'Polestar 3', 'Polestar 4', 'Other'],
  Porsche: ['718', '911', 'Taycan', 'Macan', 'Cayenne', 'Panamera', 'Other'],
  Renault: ['Clio', 'Megane', 'Arkana', 'Austral', 'Scenic', 'Espace', 'Kangoo', 'Other'],
  'Rolls-Royce': ['Ghost', 'Phantom', 'Cullinan', 'Spectre', 'Other'],
  Saab: ['9-3', '9-5', 'Other'],
  Seat: ['Ibiza', 'Leon', 'Ateca', 'Tarraco', 'Other'],
  Skoda: ['Fabia', 'Scala', 'Octavia', 'Superb', 'Kamiq', 'Karoq', 'Kodiaq', 'Enyaq', 'Other'],
  Smart: ['#1', '#3', 'Fortwo', 'Other'],
  Subaru: ['Impreza', 'Legacy', 'Outback', 'Forester', 'Crosstrek', 'Solterra', 'Other'],
  Suzuki: ['Swift', 'Baleno', 'Vitara', 'S-Cross', 'Across', 'Other'],
  Tesla: ['Model 3', 'Model Y', 'Model S', 'Model X', 'Cybertruck', 'Other'],
  Toyota: [
    'Aygo',
    'Yaris',
    'Corolla',
    'Camry',
    'Prius',
    'C-HR',
    'RAV4',
    'Highlander',
    'bZ4X',
    'Proace',
    'Other',
  ],
  Volkswagen: [
    'Polo',
    'Golf',
    'Jetta',
    'Passat',
    'Arteon',
    'T-Cross',
    'T-Roc',
    'Tiguan',
    'Touran',
    'ID.3',
    'ID.4',
    'ID.5',
    'ID.7',
    'Transporter',
    'Caddy',
    'Other',
  ],
  Volvo: ['V40', 'S60', 'S90', 'V60', 'V90', 'XC40', 'XC60', 'XC90', 'EX30', 'EX90', 'Other'],
};

export function getModelsForMake(make) {
  if (!make) return [];
  const list = VEHICLE_MODELS_BY_MAKE[make];
  if (Array.isArray(list) && list.length > 0) return list;
  return ['Other'];
}

export const VEHICLE_COLORS = [
  'Beige',
  'Black',
  'Blue',
  'Brown',
  'Burgundy',
  'Gold',
  'Gray',
  'Green',
  'Orange',
  'Purple',
  'Red',
  'Silver',
  'White',
  'Yellow',
  'Other',
];

/** Norwegian taxi / yrkestransport — official guidance (Norwegian). */
export const TAXI_OPERATING_LICENCE_INFO_URL = 'https://www.vegvesen.no/kjoretoy/eiend-og-vedlikehold/drosje/';

const START_YEAR_OFFSET = 1;
export function getVehicleYearOptions() {
  const y = new Date().getFullYear() + START_YEAR_OFFSET;
  return Array.from({ length: y - 1989 }, (_, i) => y - i);
}
